/**
 * Plan model helpers shared by the chat cards and the plan pane.
 *
 * AI Elements' `Queue` only knows `pending | completed`; the plan's four todo
 * statuses map onto it here so every rendering agrees.
 */

import { stripEntityRefs } from "@/features/agents/refs/entityRefs";

import type {
  AskUserAnswer,
  AskUserAnswers,
  AskUserQuestion,
  CaseAgentPlan,
  PlanTodo,
  PlanTodoStatus
} from "../types";

export type QueueTodoView = {
  id: string;
  content: string;
  status: PlanTodoStatus;
  /** Drives `QueueItemIndicator`/`QueueItemContent` styling. */
  completed: boolean;
  /** Show a spinner in place of the indicator. */
  active: boolean;
  /** Muted strikethrough without the "done" indicator. */
  cancelled: boolean;
};

export const todoToQueueItem = (todo: PlanTodo): QueueTodoView => ({
  id: todo.id,
  content: todo.content,
  status: todo.status,
  completed: todo.status === "completed" || todo.status === "cancelled",
  active: todo.status === "in_progress",
  cancelled: todo.status === "cancelled"
});

export const todoProgress = (
  todos: readonly PlanTodo[]
): { done: number; total: number; label: string } => {
  const total = todos.length;
  const done = todos.filter(
    (t) => t.status === "completed" || t.status === "cancelled"
  ).length;
  return { done, total, label: `${done} of ${total} done` };
};

export const PLAN_STATUS_LABEL: Record<CaseAgentPlan["status"], string> = {
  draft: "Draft",
  building: "Building",
  complete: "Complete"
};

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null;

export const todosFrom = (raw: unknown): PlanTodo[] => {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((t, i) => {
    if (typeof t === "string")
      return [{ id: `t${i + 1}`, content: t, status: "pending" as const }];
    if (!isRecord(t)) return [];
    const content = typeof t.content === "string" ? t.content : "";
    if (!content) return [];
    const { status } = t;
    return [
      {
        id: typeof t.id === "string" && t.id ? t.id : `t${i + 1}`,
        content,
        status:
          status === "in_progress" ||
          status === "completed" ||
          status === "cancelled"
            ? status
            : ("pending" as const),
        ...(typeof t.owner === "string" && t.owner ? { owner: t.owner } : {}),
        ...(typeof t.due === "string" && t.due ? { due: t.due } : {})
      }
    ];
  });
};

/** The build's own todos; the rest belong to the attorney or the client. */
export const AGENT_OWNER = "CaseBlink";
export const isAgentTodo = (todo: PlanTodo) =>
  !todo.owner || todo.owner.toLowerCase() === AGENT_OWNER.toLowerCase();

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** `2026-10-16` → "Oct 16, 2026" (or "Oct 16" this year); other text as is. */
export const formatDueDate = (
  value: string,
  today: Date = new Date()
): string => {
  const m = ISO_DATE.exec(value);
  if (!m) return value;
  const date = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    ...(date.getFullYear() === today.getFullYear() ? {} : { year: "numeric" })
  });
};

/** Before today, by calendar day. */
export const isOverdue = (value: string, today: Date = new Date()): boolean => {
  const m = ISO_DATE.exec(value);
  if (!m) return false;
  const due = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const start = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  );
  return due < start;
};

/** The plan out of a `create_plan` / `update_plan` tool output, if any. */
export const planFromToolOutput = (output: unknown): CaseAgentPlan | null => {
  if (!isRecord(output) || !isRecord(output.plan)) return null;
  const p = output.plan;
  if (typeof p.id !== "string" || typeof p.title !== "string") return null;
  return {
    id: p.id,
    thread_id: String(p.thread_id ?? ""),
    individual_id: String(p.individual_id ?? ""),
    title: p.title,
    overview: typeof p.overview === "string" ? p.overview : "",
    markdown: typeof p.markdown === "string" ? p.markdown : "",
    todos: todosFrom(p.todos),
    status:
      p.status === "building" || p.status === "complete" ? p.status : "draft",
    saved_to_workspace: p.saved_to_workspace === true,
    created_at: typeof p.created_at === "string" ? p.created_at : null,
    updated_at: typeof p.updated_at === "string" ? p.updated_at : null
  };
};

/** The todo list out of an `update_todos` tool output. */
export const todosFromToolOutput = (
  output: unknown
): {
  planId: string | null;
  title: string | null;
  todos: PlanTodo[];
} | null => {
  if (!isRecord(output) || !Array.isArray(output.todos)) return null;
  return {
    planId: typeof output.plan_id === "string" ? output.plan_id : null,
    title: typeof output.title === "string" ? output.title : null,
    todos: todosFrom(output.todos)
  };
};

/** The questions out of an `ask_user` tool input, tolerant of partial streaming. */
/** The card is plain text; a record link the model pasted in shows as its name. */
const plain = (text: string): string => stripEntityRefs(text).trim();

export const questionsFromToolInput = (
  input: unknown
): { title: string | null; questions: AskUserQuestion[] } => {
  if (!isRecord(input) || !Array.isArray(input.questions))
    return { title: null, questions: [] };
  const questions = input.questions.flatMap((q, i): AskUserQuestion[] => {
    if (!isRecord(q)) return [];
    const prompt = plain(
      typeof q.prompt === "string"
        ? q.prompt
        : typeof q.question === "string"
        ? q.question
        : ""
    );
    if (!prompt) return [];
    const rawChoices = Array.isArray(q.choices)
      ? q.choices
      : Array.isArray(q.options)
      ? q.options
      : [];
    const choices = rawChoices.flatMap((c, j) => {
      if (typeof c === "string")
        return [{ value: `c${j + 1}`, label: plain(c) }];
      if (!isRecord(c)) return [];
      const label = plain(
        typeof c.label === "string"
          ? c.label
          : typeof c.value === "string"
          ? c.value
          : ""
      );
      if (!label) return [];
      return [
        {
          value: typeof c.value === "string" && c.value ? c.value : `c${j + 1}`,
          label,
          description:
            typeof c.description === "string"
              ? plain(c.description)
              : undefined,
          ...(c.recommended === true ? { recommended: true } : {})
        }
      ];
    });
    const allowOther = q.allow_other ?? q.allow_freeform;
    return [
      {
        name: typeof q.name === "string" && q.name ? q.name : `q${i + 1}`,
        prompt,
        description:
          typeof q.description === "string" ? plain(q.description) : undefined,
        choices,
        multiple: Boolean(q.multiple ?? q.allow_multiple),
        required: q.required !== false,
        allow_other: allowOther === undefined ? true : Boolean(allowOther),
        ...(q.kind === "date" ? { kind: "date" as const } : {})
      }
    ];
  });
  return {
    title: typeof input.title === "string" ? plain(input.title) : null,
    questions
  };
};

/**
 * Read a submitted questionnaire form into `{name: answer}`. A choice value
 * and a typed "other" answer share the item's field name, so `getAll` covers
 * both; a single-choice item with one value collapses to a string.
 */
export const answersFromFormData = (
  form: FormData,
  questions: readonly AskUserQuestion[]
): AskUserAnswers => {
  const out: AskUserAnswers = {};
  for (const q of questions) {
    const values = form
      .getAll(q.name)
      .map((v) => (typeof v === "string" ? v.trim() : ""))
      .filter(Boolean);
    if (values.length)
      out[q.name] = q.multiple ? values : values[values.length - 1];
  }
  return out;
};

/**
 * Every question's answer, `null` for each one the attorney skipped, so the
 * agent can tell a skipped question from an answered one.
 */
export const withSkips = (
  answers: AskUserAnswers,
  questions: readonly AskUserQuestion[]
): AskUserAnswers =>
  Object.fromEntries(questions.map((q) => [q.name, answers[q.name] ?? null]));

/** No answer recorded: skipped on the card (the agent's result leaves it out). */
export const isSkippedAnswer = (
  answer: AskUserAnswer | null | undefined
): boolean => answer == null || (Array.isArray(answer) && !answer.length);

/** Human-readable rendering of one answer using the question's choice labels. */
export const describeAnswer = (
  question: AskUserQuestion,
  answer: AskUserAnswer | null | undefined
): string => {
  if (answer == null) return "Skipped";
  const values = Array.isArray(answer) ? answer : [answer];
  if (!values.length) return "Skipped";
  const labels = values.map(
    (v) =>
      question.choices.find((c) => c.value === v)?.label ??
      (question.kind === "date" ? formatDueDate(v) : v)
  );
  return labels.join(", ");
};
